import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buildApiUrl } from '../lib/api';
import {
  asContentArray,
  asContentObject,
  fetchContentBlocks,
  getContentValue,
} from '../lib/content';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRef(null);
  const { data: contentBlocks = [] } = useQuery({
    queryKey: ['siteContent'],
    queryFn: fetchContentBlocks,
  });
  const contactValue = getContentValue(contentBlocks, 'contact', {});
  const contactContent = asContentObject(contactValue, {});
  const contactDetails = asContentArray(contactContent.details);
  const contactEyebrow = contactContent.eyebrow || 'Contact / new work / introductions';
  const contactTitle = typeof contactContent.title === 'string' ? contactContent.title.trim() : '';
  const contactSubtitle = contactContent.subtitle || 'We usually reply within one business day.';
  const contactBestFor = contactContent.bestFor || '';
  const contactResponseWindow = contactContent.responseWindow || '';
  const messagePlaceholder = contactContent.messagePlaceholder || 'type in your inquiry';
  const hasContactMeta = Boolean(contactBestFor || contactResponseWindow || contactDetails.length > 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(buildApiUrl('/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || 'Failed to send message');
      }

      setSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
      successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page-shell">
      <div className="page-width">
        <section className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="surface-card p-8 md:p-10">
            <p className="eyebrow normal-case">{contactEyebrow}</p>
            {contactTitle && (
              <h1 className="mt-6 font-display text-5xl leading-none tracking-tight text-slate-900 md:text-6xl">
                {contactTitle}
              </h1>
            )}
            <p className={`${contactTitle ? 'mt-6' : 'mt-4'} text-sm leading-8 text-slate-600`}>
              {contactSubtitle}
            </p>

            {hasContactMeta && (
              <div className="section-divider mt-8 space-y-5 pt-6">
                {contactBestFor && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Best for</p>
                    <p className="mt-2 text-sm leading-7 text-slate-700">{contactBestFor}</p>
                  </div>
                )}
                {contactResponseWindow && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Response window</p>
                    <p className="mt-2 text-sm leading-7 text-slate-700">{contactResponseWindow}</p>
                  </div>
                )}
                {contactDetails.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Notes</p>
                    <div className="mt-2 space-y-2">
                      {contactDetails.map((detail, index) => (
                        <p key={`${detail}-${index}`} className="text-sm leading-7 text-slate-700">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="surface-card p-8 md:p-10">
            {submitted && (
              <div
                ref={successRef}
                className="mb-8 rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm leading-7 text-emerald-800"
              >
                Message sent successfully. We will get back to you soon.
              </div>
            )}

            {error && (
              <div className="mb-8 rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm leading-7 text-rose-800">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="field-label">
                    First name *
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="field-input"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="field-label">
                    Last name *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="field-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="field-label">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="field-input"
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="company" className="field-label">
                    Company
                  </label>
                  <input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="field-label">
                    Phone
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="field-label">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="field-input field-textarea"
                  placeholder={messagePlaceholder}
                />
              </div>

              <button type="submit" disabled={loading} className="primary-button w-full disabled:translate-y-0 disabled:bg-slate-300">
                {loading ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}
